// utils/encrypt.js

import {
  encryptMessage,
  encryptWithGroupKey,
  encryptWithStatusKey,
} from "./Cryptoutils";

/**
 * Encrypts a DM text or media payload for both recipient and sender.
 *
 * @param {string} payload          - Plaintext string to encrypt
 * @param {CryptoKey} recipientKey  - Recipient's public key
 * @param {CryptoKey} senderKey     - Sender's own public key
 * @returns {{ forRecipient, forSender } | null}
 */
export async function encryptDualDm(
  payload,
  recipientKey,
  senderKey
) {

  const recipientPayload =
    typeof payload === "object" &&
    payload?.forRecipient !== undefined
      ? payload.forRecipient
      : payload;

  const senderPayload =
    typeof payload === "object" &&
    payload?.forSender !== undefined
      ? payload.forSender
      : payload;

  const [
    forRecipient,
    forSender,
  ] = await Promise.all([

    encryptMessage(
      recipientPayload,
      recipientKey
    ),

    encryptMessage(
      senderPayload,
      senderKey
    ),

  ]);

  if (
    !forRecipient ||
    !forSender
  ) {
    return null;
  }

  return {
    forRecipient,
    forSender,
  };
}

/**
 * Encrypts a string with a symmetric group key.
 *
 * @param {string} payload   - Plaintext string to encrypt
 * @param {CryptoKey} groupKey
 * @returns {{ ciphertext, iv } | null}
 */
export async function encryptGroup(payload, groupKey) {
  const enc = await encryptWithGroupKey(payload, groupKey);
  if (!enc?.ciphertext || !enc?.iv) return null;
  return { ciphertext: enc.ciphertext, iv: enc.iv };
}

/**
 * Encrypts status content with the shared
 * AES status key.
 *
 * Returns:
 * {
 *   ciphertext,
 *   iv,
 *   for: "owner"
 * }
 */
export async function encryptStatus(
  payload,
  statusKey
) {

  if (!payload || !statusKey) {
    return null;
  }

  const envelope =
    await encryptWithStatusKey(
      payload,
      statusKey
    );

  if (!envelope) {
    return null;
  }

  return {

    ...envelope,

    for: "owner",

  };

}

/**
 * Encrypts a status caption.
 *
 * Convenience wrapper around encryptStatus().
 */
export async function encryptStatusCaption(
  caption,
  statusKey
) {

  return encryptStatus(
    caption,
    statusKey
  );

}

/**
 * Encrypts a status media reference.
 *
 * The payload should already be serialised
 * (for example the uploaded media URL or metadata).
 */
export async function encryptStatusMedia(
  payload,
  statusKey
) {

  return encryptStatus(
    payload,
    statusKey
  );

}

/**
 * Encrypts a status thumbnail.
 *
 * The payload should already be serialised.
 */
export async function encryptStatusThumbnail(
  payload,
  statusKey
) {

  return encryptStatus(
    payload,
    statusKey
  );

}

/**
 * Encrypts a DM reply preview for both recipient and sender.
 * Returns serialised JSON strings ready to attach to the payload.
 *
 * @param {object|null} preview      - Result of buildReplyPreview()
 * @param {CryptoKey} recipientKey
 * @param {CryptoKey} senderKey
 * @returns {{ replyPreviewEncrypted: string|null, replyPreviewEncryptedSender: string|null }}
 */
export async function encryptDmReplyPreview(preview, recipientKey, senderKey) {
  if (!preview || !recipientKey || !senderKey) {
    return { replyPreviewEncrypted: null, replyPreviewEncryptedSender: null };
  }

  const serialised = JSON.stringify(preview);
  const [forRecipient, forSender] = await Promise.all([
    encryptMessage(serialised, recipientKey),
    encryptMessage(serialised, senderKey),
  ]);

  return {
    replyPreviewEncrypted: forRecipient ? JSON.stringify(forRecipient) : null,
    replyPreviewEncryptedSender: forSender ? JSON.stringify(forSender) : null,
  };
}

/**
 * Encrypts a group reply preview with the group key.
 * Returns a serialised JSON string ready to attach to the payload.
 *
 * @param {string} previewText
 * @param {CryptoKey} groupKey
 * @returns {string|null}
 */
export async function encryptGroupReplyPreview(previewText, groupKey) {
  if (!previewText || !groupKey) return null;

  const enc = await encryptWithGroupKey(previewText, groupKey);
  if (!enc?.ciphertext || !enc?.iv) return null;

  return JSON.stringify({ ciphertext: enc.ciphertext, iv: enc.iv });
}

export async function encryptStatusReplyPreview(
  preview,
  recipientKey,
  senderKey
) {

  if (
    !preview ||
    !recipientKey ||
    !senderKey
  ) {

    return {

      replyPreviewEncrypted: null,

      replyPreviewEncryptedSender: null,

    };

  }

  const serialised =
    JSON.stringify(preview);

  const [

    forRecipient,

    forSender,

  ] = await Promise.all([

    encryptMessage(
      serialised,
      recipientKey
    ),

    encryptMessage(
      serialised,
      senderKey
    ),

  ]);

  return {

    replyPreviewEncrypted:
      forRecipient
        ? JSON.stringify(forRecipient)
        : null,

    replyPreviewEncryptedSender:
      forSender
        ? JSON.stringify(forSender)
        : null,

  };

}